import { Button } from "@/components/ui/button";
import CreateInvoiceForm, { type InvoicePrefill } from "./CreateInvoiceForm";
import { useUpsells } from "@/hooks/use-upsells";
import type { DBProject, DBInvoice } from "@/lib/db-types";
import type { CustomerInfo } from "@/hooks/use-project-detail";
import { formatNOK } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { useInvoiceCalculations } from "@/hooks/use-invoice-calculations";
import { useProjectPermission } from "@/hooks/use-project-permission";
import { RemainingToInvoiceCard } from "./RemainingToInvoiceCard";
import { InvoicesList } from "./InvoicesList";
import { TMInvoiceSection } from "./TMInvoiceSection";
import { InterimInvoiceSection } from "./InterimInvoiceSection";
import { computeTermLocks } from "@/lib/invoice-plan";
import { useFeatureFlag } from "@/hooks/use-feature-flag";

export default function FakturaTab({
  project: p,
  invoices,
  customer,
  showCreateInvoice,
  invoicePrefill,
  onOpenCreate,
  onCloseCreate,
}: {
  project: DBProject;
  invoices: DBInvoice[];
  /** Kundekortet: fallback for fakturadialogens referanser og e-post. */
  customer: CustomerInfo | null;
  showCreateInvoice: boolean;
  invoicePrefill: InvoicePrefill | null;
  onOpenCreate: (prefill?: InvoicePrefill) => void;
  onCloseCreate: () => void;
}) {
  const { data: upsells } = useUpsells(p.id);
  const allUpsells = upsells ?? [];
  // VIB-722c: create_invoice gates "Opprett faktura"-knappene.
  // Admin/superadmin overstyrer; default = project_lead + client_advisor=true.
  const canCreateInvoice = useProjectPermission(p.id, "create_invoice");

  const calc = useInvoiceCalculations(p, invoices, allUpsells);

  // VIB-994: T&M-mersalg på et ellers fastpris-prosjekt — én egen
  // T&M-fakturaseksjon per GODKJENT T&M-mersalg (blokkerer fakturering av
  // ikke-godkjent mersalg i UI, jf. VIB-665-gaten). Draft T&M-mersalg vises
  // ikke her (synlig i Mersalg-fanen med "Godkjenn"-knapp som vanlig). Hele
  // grenen er bak feature_tm_upsell — et rent kill-switch: slår Erik flagget
  // av igjen, forsvinner fakturerings-UI-et selv om et gammelt T&M-mersalg
  // finnes (badge/"fakturert hittil" i Mersalg-fanen er upåvirket — kun
  // opprettelse av NYE T&M-fakturaer gates her).
  const tmUpsellEnabled = useFeatureFlag("feature_tm_upsell");
  const approvedTMUpsells = tmUpsellEnabled
    ? allUpsells.filter((u) => u.billing_mode === "tm" && u.status === "approved")
    : [];

  // VIB-800: T&M-prosjekter har egen faktura-flyt (per-ansatt timer + bilag),
  // ikke fastpris-kortene (oppstart/milepæl/rest-kontrakt).
  if (p.project_type === "hourly") {
    return (
      <div className="space-y-4">
        <TMInvoiceSection
          projectId={p.id}
          projectName={p.name}
          canCreateInvoice={canCreateInvoice}
          onProceed={(prefill) => onOpenCreate(prefill)}
        />
        {showCreateInvoice && canCreateInvoice && (
          <CreateInvoiceForm
            project={p}
            customer={customer}
            onClose={onCloseCreate}
            prefill={invoicePrefill}
            projectInvoices={invoices}
          />
        )}
        <InvoicesList
          projectId={p.id}
          invoices={invoices}
          mainCustomer={customer}
          showCreateInvoice={false}
          onOpenCreate={() => undefined}
        />
      </div>
    );
  }

  // VIB-1182: så lenge en planlagt delfaktura (oppstart/milepæl) gjenstår,
  // skjuler vi "Opprett sluttfaktura" i Gjenstående-kortet — ellers konkurrerer
  // den med oppstarts-/milepæl-knappene og forvirrer (jf. feedback).
  const startupPending =
    !p.is_internal && !calc.hasStartupInvoice && calc.startupPct > 0 && calc.contractAmount > 0;
  const milestonePending =
    calc.hasStartupInvoice &&
    calc.nextMilestone != null &&
    calc.contractAmount > 0 &&
    calc.nextMilestoneAmount > 0;
  // VIB-2836: ad hoc-mellomfaktura når oppstarten er passert (sendt eller 0 %),
  // sluttfakturaen ikke er sendt og noe gjenstår av kontrakten.
  const adHocInterimAllowed =
    !p.is_internal &&
    calc.contractAmount > 0 &&
    calc.remainingContract > 0 &&
    (calc.hasStartupInvoice || calc.startupPct === 0) &&
    !computeTermLocks(invoices).finalInvoiced;
  const plannedInvoicesRemaining = startupPending || milestonePending;

  return (
    <div className="space-y-4">
      {/* Oppstartsfaktura-advarsel */}
      {canCreateInvoice && !p.is_internal && !calc.hasStartupInvoice && calc.startupPct > 0 && calc.contractAmount > 0 && (
        <div className="flex flex-col gap-2 p-4 rounded-xl border-2 border-green-300 bg-green-50 text-green-900">
          <div className="flex items-start gap-3">
            <SrIcon name="alert-triangle" className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-sm">Oppstartsfaktura kan sendes</p>
              <p className="text-xs text-green-800 mt-1">
                Tilbudet er godkjent. Du kan umiddelbart sende oppstartsfaktura ({calc.startupPct}%)
                på {formatNOK(calc.startupAmount)}.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="default"
            className="self-start h-8 text-xs bg-green-600 hover:bg-green-700 text-white"
            onClick={() =>
              onOpenCreate({
                amount: calc.startupAmount,
                description: `Oppstartsfaktura ${p.name} (${calc.startupPct}%)`,
                type: "startup",
              })
            }
          >
            Opprett oppstartsfaktura
          </Button>
        </div>
      )}

      {/* VIB-686/2836: mellomfakturaer — neste planlagte ledd og ad hoc utenfor planen. */}
      {canCreateInvoice && (
        <InterimInvoiceSection
          projectName={p.name}
          calc={calc}
          showPlanned={milestonePending}
          showAdHoc={adHocInterimAllowed}
          onOpenCreate={onOpenCreate}
        />
      )}

      {/* VIB-994: T&M-mersalg (godkjent) — egen fakturaseksjon per mersalg,
          scoped til mersalgets linjer (timer + bilag). */}
      {approvedTMUpsells.map((u) => (
        <TMInvoiceSection
          key={u.id}
          projectId={p.id}
          projectName={p.name}
          upsellId={u.id}
          upsellDescription={u.description}
          canCreateInvoice={canCreateInvoice}
          onProceed={(prefill) => onOpenCreate(prefill)}
        />
      ))}

      {/* Gjenstående til fakturering */}
      <RemainingToInvoiceCard
        projectId={p.id}
        projectName={p.name}
        invoices={invoices}
        uninvoicedUpsells={calc.uninvoicedUpsells}
        allUpsellsCount={allUpsells.length}
        remainingContract={calc.remainingContract}
        contractExclUpsells={calc.contractExclUpsells}
        invoicedExclVat={calc.invoicedExclVat}
        uninvoicedUpsellTotal={calc.uninvoicedUpsellTotal}
        totalRemaining={calc.totalRemaining}
        onOpenCreate={onOpenCreate}
        canCreateInvoice={canCreateInvoice}
        plannedInvoicesRemaining={plannedInvoicesRemaining}
      />

      {/* Opprett faktura form */}
      {showCreateInvoice && canCreateInvoice && (
        <CreateInvoiceForm
          project={p}
          customer={customer}
          onClose={onCloseCreate}
          prefill={invoicePrefill}
          projectInvoices={invoices}
        />
      )}

      {/* Fakturaliste */}
      <InvoicesList
        projectId={p.id}
        invoices={invoices}
        mainCustomer={customer}
        showCreateInvoice={showCreateInvoice && canCreateInvoice}
        onOpenCreate={canCreateInvoice ? onOpenCreate : () => undefined}
      />
    </div>
  );
}
